// src/components/pages/mypage/partials/PortfolioTab.js

import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../style/MyPage.module.css';
import { useNavigate } from 'react-router-dom';

export default function PortfolioTab({
  portfolios,
  loadingPortfolios,
  averageReturn,
  averageSharpe,
  auth, // 인증 상태 (버튼 표시용)
  setShowNewPortfolioModal,
  handleOpenRebalanceModal,
  handleDeletePortfolio,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 포트폴리오를 ALL (성과 추적 및 공개)와 PRIVATE (개인 보관용)로 분류합니다.
  const allPortfolios = portfolios.filter(p => p.portfolioType === 'ALL');
  const privatePortfolios = portfolios.filter(p => p.portfolioType === 'PRIVATE'); 

  const isLoadingOrEmpty = loadingPortfolios || portfolios.length === 0;

  return (
    <div className={styles.tabContent}>
      <section className={styles.portfolioSummary}>
        {/* 포트폴리오 요약 섹션 (생략 없음) */}
        {loadingPortfolios ? (
          <p>{t('mypage.loading.generic')}</p>
        ) : portfolios.length === 0 ? (
          <p>{t('mypage.noPortfolioRegistered')}</p>
        ) : (
          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <strong>{t('mypage.avg1yReturn')}:</strong>
              <span>{(averageReturn * 100).toFixed(2)}%</span>
            </div>
            <div className={styles.summaryItem}>
              <strong>{t('mypage.avg1ySharpe')}:</strong>
              <span>{(averageSharpe).toFixed(2)}</span>
            </div>
          </div>
        )}
      </section>

      <section className={styles.list}>
        {/* 🚨 1. 성과 추적 및 공개 포트폴리오 (ALL) 섹션 (변경 없음) */}
        <h3 className={styles.sectionTitle}>
          {t('mypage.publicTrackingPortfolios') || '성과 추적 및 공개 포트폴리오 (ALL)'} ({allPortfolios.length})
        </h3>
        {loadingPortfolios ? (
          <p>{t('mypage.loading.generic')}</p>
        ) : allPortfolios.length === 0 ? (
          <p>{t('mypage.noAllPortfolios') || '현재 공개된 성과 추적 포트폴리오가 없습니다.'}</p>
        ) : (
          <table className={styles.portfolioTable}>
            <thead>
              <tr>
                <th>{t('mypage.name')}</th>
                <th>{t('mypage.table.ret1y')}</th>
                <th>{t('mypage.table.sr1y')}</th>
                <th>{t('mypage.elapsedDays')}</th>
                <th>{t('mypage.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {allPortfolios.map(p => (
                <tr key={p.portfolioId} onClick={() => navigate(`/portfolios/${p.portfolioId}`)} className={styles.tableRowClickable}>
                  <td>{p.name || t('mypage.unnamedPortfolio')}</td>
                  <td>{(p.ret_1y * 100 || 0).toFixed(2)}%</td>
                  <td>{(p.sharpe_1y || 0).toFixed(2)}</td>
                  <td>{Math.floor((new Date() - new Date(p.createdAt)) / (1000 * 60 * 60 * 24))}{t('mypage.dateSuffix')}</td>
                  <td>
                    <button
                      className={styles.actionBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenRebalanceModal(p);
                      }}
                    >
                      🔄
                      <span className={styles.tooltip}>{t('mypage.rebalance')}</span>
                    </button>
                    <button
                      className={styles.actionBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePortfolio(p.portfolioId);
                      }}
                    >
                      🗑️
                      <span className={styles.tooltip}>{t('mypage.delete')}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* --- 구분선 --- */}
        <div style={{ margin: '30px 0', borderTop: '1px solid #ddd' }}></div>

        {/* 🚨 2. 개인 보관용 포트폴리오 (PRIVATE) 섹션 (수정됨) */}
        <h3 className={styles.sectionTitle}>
          {t('mypage.privateArchivedPortfolios') || '개인 보관용 포트폴리오 (PRIVATE) (성과 추적 안 함)'} ({privatePortfolios.length})
        </h3>
        {loadingPortfolios ? (
          <p>{t('mypage.loading.generic')}</p>
        ) : privatePortfolios.length === 0 ? (
          <p>{t('mypage.noPrivatePortfolios') || '현재 개인 보관용 포트폴리오가 없습니다. (백테스트 후 저장된 포트폴리오)'}</p>
        ) : (
          <table className={styles.portfolioTable}>
            <thead>
              <tr>
                <th>{t('mypage.name')}</th>
                <th>{t('mypage.actions')}</th> 
              </tr>
            </thead>
            <tbody>
              {privatePortfolios.map(p => (
                <tr key={p.portfolioId} >
                  <td>{p.name || t('mypage.unnamedPortfolio')}</td>
                  <td>
                    <button
                      className={styles.actionBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenRebalanceModal(p); // PRIVATE 포트폴리오 그대로 전달
                      }}
                    >
                      🔄
                      <span className={styles.tooltip}>{t('mypage.rebalance')}</span>
                    </button>
                    <button
                      className={styles.actionBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePortfolio(p.portfolioId);
                      }}
                    >
                      🗑️
                      <span className={styles.tooltip}>{t('mypage.delete')}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <br />
        <div className={styles.portfolioActions}>
          {auth.isAuthenticated && (
            <button onClick={() => setShowNewPortfolioModal(true)} className={styles.editBtn}>
              {t('mypage.registerNewPortfolio')}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}